// Reads the runtime config injected by /env.js (see index.html + shell-entrypoint.sh).
export interface CompathConfig {
  oidcAuthority: string;
  oidcClientId: string;
  apiBase: string;
  // "oidc"  = shell runs the Authorization Code + PKCE flow itself (local dev / standalone).
  // "edge"  = an edge proxy (oauth2-proxy) already authenticated the request and injects the bearer;
  //           the shell just reads identity from /api/me and never touches OIDC.
  // "dev"   = local dev BYPASS: no login at all. The backend (compath.dev.auth.enabled) injects a
  //           fixed demo identity, so the shell skips OIDC, sends no bearer, and reads /api/me directly.
  //           Optional persona switch via the X-Dev-User header (see devUser).
  authMode: "oidc" | "edge" | "dev";
  // Where the shell's "로그아웃" navigates in edge mode (edge session end → IdP end-session).
  logoutUrl: string;
  // "dev" mode only: which demo persona to act as (X-Dev-User header). Default "kim".
  // Switchable from the browser with ?devUser=<persona> — see resolveDevUser.
  devUser: string;
}

declare global {
  interface Window {
    __COMPATH__?: Partial<CompathConfig>;
  }
}

const raw = window.__COMPATH__ ?? {};

// Vite injects build/env flags on import.meta.env; read it via a local cast so this file needs no
// extra ambient type reference (vite/client) to typecheck.
const viteEnv = (import.meta as unknown as { env?: Record<string, string | boolean | undefined> })
  .env ?? {};

// Auth mode precedence: an explicit value from /env.js wins; otherwise VITE_AUTH_MODE; otherwise
// `vite dev` (import.meta.env.DEV) defaults to the local "dev" bypass, and any real build to "oidc".
// This keeps the oidc/edge code paths untouched — they engage exactly when configured as before.
function resolveAuthMode(): CompathConfig["authMode"] {
  const explicit = raw.authMode;
  if (explicit === "edge" || explicit === "oidc" || explicit === "dev") return explicit;
  const envMode = viteEnv.VITE_AUTH_MODE;
  if (envMode === "edge" || envMode === "oidc" || envMode === "dev") return envMode;
  return viteEnv.DEV ? "dev" : "oidc";
}

const DEV_USER_KEY = "compath.devUser";

// Which demo persona the dev bypass acts as. Precedence: `?devUser=<persona>` in the URL (remembered
// in localStorage for subsequent navigations) > /env.js or VITE_DEV_USER > "kim". The URL switch
// exists so screens gated on another identity — 워크스페이스 관리 (workspace admin: `acme`), 관리자
// (platform admin: `hong`) — can be opened in a plain browser without editing env.js and restarting.
// Only honoured in the dev bypass; oidc/edge take their identity from the real token.
function resolveDevUser(mode: CompathConfig["authMode"]): string {
  const configured = raw.devUser ?? (viteEnv.VITE_DEV_USER as string | undefined) ?? "kim";
  if (mode !== "dev") return configured;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("devUser");
    if (fromUrl) {
      window.localStorage.setItem(DEV_USER_KEY, fromUrl);
      return fromUrl;
    }
    return window.localStorage.getItem(DEV_USER_KEY) ?? configured;
  } catch {
    // Storage can be unavailable (private mode); the configured persona is always a valid fallback.
    return configured;
  }
}

const authMode = resolveAuthMode();

export const config: CompathConfig = {
  oidcAuthority: raw.oidcAuthority ?? "http://localhost:8180/realms/compath",
  oidcClientId: raw.oidcClientId ?? "compath-shell",
  apiBase: raw.apiBase ?? "",
  authMode,
  logoutUrl: raw.logoutUrl ?? "/oauth2/sign_out",
  devUser: resolveDevUser(authMode),
};

export const redirectUri = `${window.location.origin}/callback`;
